import { AccountCredentialsSection } from './AccountCredentialsSection/AccountCredentials.section';
import { DeleteUserModalContainer } from './DeleteUserModalContainer/DeleteUserModalContainer';
import { PersonalInformationSection } from './PersonalInformationSection/PersonalInformation.section';
import { ProfileSettingsSection } from './ProfileSettingsSection/ProfileSettings.section';

import { ThemeProvider } from '@aircall/tractor-v2';
import { DetailPage } from '@components/DetailPage/DetailPage';
import { CRITICAL_ACTION } from '@constants/critical-action.constants';
import { Loading } from '@dashboard/library';
import { canDeleteUser } from '@helpers/permissions.helpers';
import { useAuthStrategy } from '@hooks/useAuthStrategy/useAuthStrategy';
import { useCheckCriticalAction } from '@hooks/useCheckCriticalAction';
import { useFeatures } from '@hooks/useFeatures/useFeatures';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { usePermissions } from '@hooks/usePermissions';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { AUTH_STRATEGY } from '@state/app/authentication/authentication.decl';

export function UserProfileGeneral({ user }: UserProfileTabProps) {
  const { currentUser } = useGlobalData();
  const { strategies, privilegedUserIds } = useAuthStrategy();
  const { roles } = usePermissions();
  const { fraudEmailChangeValidation, fraudPasswordChangeValidation } = useFeatures();
  const { data, loading } = useCheckCriticalAction(
    CRITICAL_ACTION.UPDATE_EMAIL,
    user.ID,
    user.email,
    !user.migratedOnAuthenticationManager || !fraudEmailChangeValidation
  );

  if (loading) {
    return (
      <ThemeProvider>
        <Loading data-test='user-profile-general-loading' />
      </ThemeProvider>
    );
  }

  const onGoingUpdateEmailCriticalAction = Boolean(data?.checkCriticalAction?.active);

  const isCurrentUser = user.ID === currentUser.ID;
  const shouldHidePasswordUpdate =
    strategies.includes(AUTH_STRATEGY.GOOGLE) &&
    !privilegedUserIds.includes(parseInt(currentUser.ID));
  const displayDelete = canDeleteUser(currentUser.ID, roles, user.ID, user.roles);

  return (
    <DetailPage data-test='user-profile-general'>
      <PersonalInformationSection user={user} isCurrentUser={isCurrentUser} />

      <ProfileSettingsSection user={user} isCurrentUser={isCurrentUser} />

      <AccountCredentialsSection
        user={user}
        isCurrentUser={isCurrentUser}
        shouldHidePasswordUpdate={shouldHidePasswordUpdate}
        onGoingUpdateEmailCriticalAction={onGoingUpdateEmailCriticalAction}
        fraudEmailChangeValidation={fraudEmailChangeValidation}
        fraudPasswordChangeValidation={fraudPasswordChangeValidation}
      />

      {displayDelete && <DeleteUserModalContainer user={user} roles={user.roles} />}
    </DetailPage>
  );
}
